<template>
  <div class="partner-page">
    <div class="container pt-0 pb-0">
      <div class="partner-page__top-menu">
        <div class="partner-page__top-menu__nav__prev">
          <NuxtLink class="btn btn-link" type="button" href="/partners">
            <IconBack class="icon" />
            <span class="text">Back</span>
          </NuxtLink>
        </div>
        <div class="partner-page__top-menu__nav">
          <div class="partner-page__top-menu__nav__prev">
            <NuxtLink class="btn btn-link" type="button" :href="prevPartnerUrl">
              <IconChevronLeft class="icon" />
              <span class="text">Previous</span>
            </NuxtLink>
          </div>
          <div class="partner-page__top-menu__nav__next">
            <NuxtLink class="btn btn-link" type="button" :href="nextPartnerUrl">
              <span class="text">Next</span>
              <IconChevronLeft class="icon icon-reverse" />
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid header-container">
      <div class="row">
        <div class="col-lg-7 left-col" data-aos="fade-in">
          <div class="inner-container">
            <div class="modality" v-if="data.modality">
              <SmartLinkStandaloneElement :codename="type.elements.modality.codename">
                <p>{{ data.modality }}</p>
              </SmartLinkStandaloneElement>
            </div>
            <div class="title" v-if="data.title">
              <SmartLinkStandaloneElement :codename="type.elements.partner_title.codename">
                <h2>{{ data.title }}</h2>
              </SmartLinkStandaloneElement>
            </div>
          </div>
        </div>
        <div class="col-lg-5 right-col" data-aos="fade-in">
          <div class="inner-container">
            <div class="logo" v-if="firstOrDefault(data.logo)">
              <NuxtImg :src="firstOrDefault(data.logo)?.url" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container pt-0">
      <hr class="divider" />
      <div class="row">
        <div class="col-lg-6" data-aos="fade-in">
          <div class="long-description">
            <SmartLinkStandaloneElement :codename="type.elements.long_description.codename">
              <RichText v-if="!isEmpty(data.longDescription)" :value="data.longDescription" />
            </SmartLinkStandaloneElement>
          </div>
          <div class="cta" v-for="cta in data.ctas">
            <CallToAction :model="cta" />
          </div>
        </div>
        <div class="offset-lg-1 col-lg-5" data-aos="fade-in">
          <div class="modules" v-if="data.modules.length > 0">
            <div class="header">Modules</div>
            <ul class="link-list">
              <li v-for="module in data.modules">
                <NuxtLink class="link" :to="module.link">
                  <span class="text">{{ module.elements.module_title.value }}</span>
                  <IconArrowRightLong class="icon" />
                </NuxtLink>
              </li>
            </ul>
          </div>
          <div class="collaborators" v-if="data.collaborators.length > 0">
            <div class="header">Collaborators</div>
            <ul class="link-list">
              <li v-for="item in data.collaborators">
                <NuxtLink class="link" :to="item.link">
                  <span class="text">{{ item.elements.partner_title.value }}</span>
                  <IconArrowRightLong class="icon" />
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
  <SocialSharePreviewBlock v-if="page.elements.social_share_preview.value.length > 0" 
                                 :model="pageStore.getLinkedItem<SocialSharePreviewModel>(page.elements.social_share_preview.value[0]!)"/>
</template>

<script lang="ts" setup>
import { contentTypes, type CallToActionModel, type PartnerItemModel, type SocialSharePreviewModel } from '~~/app/models'
import type { ModuleItemWithLink } from '~~/app/types/ModuleWithLink'
import type { PartnerItemWithLink } from '~~/app/types/PartnerWithLink'
import IconBack from '../icons/IconBack.vue'
const type = contentTypes.partner_item

const pageStore = usePageStore()
const modalityFilterStore = useModalityFilter()

const page = pageStore.page as PartnerItemModel

const { setStateValue } = usePartnerState()

setStateValue('partner')

const data = {
  title: page.elements.partner_title.value,
  modality: getModality(page),
  collaborators: pageStore.getListAs<PartnerItemWithLink>(page.elements.collaborators.value),
  modules: pageStore.getListAs<ModuleItemWithLink>(page.elements.modules.value),
  logo: page.elements.logo.value,
  longDescription: page.elements.long_description.value,
  shortDescription: page.elements.short_description.value,
  ctas: pageStore.getListAs<CallToActionModel>(page?.elements?.call_to_action?.value)
}
function getModality(item: PartnerItemModel) {
  if (firstOrDefault(item.elements.modality.value)) {
    return item.elements.modality.value
      .map((x) => x.codename.replace('_module', '').split('_').join(' '))
      .join(', ')
  }
  return ''
}

const links = await useFetch(`/api/partners-routes`)

links.data.value?.data?.forEach((result) => {
  const item = data.collaborators.find((x: any) => x.system.codename == result.codename)
  if (item) {
    item.link = result.path
  }
})

links.data.value?.data?.forEach((result) => {
  const item = data.modules.find((x: any) => x.system.codename == result.codename)
  if (item) {
    item.link = result.path
  }
})

const partners = await useFetch(`/api/partners`)
const partnersWithModalities = (partners.data?.value as any).data as any[]
const partnerFilter = modalityFilterStore.get()
const partnersFiltered = partnersWithModalities.filter((p) =>
  p.modalities.find((modality: any) =>
    (partnerFilter.length > 0 ? partnerFilter : [modality.codename]).includes(modality.codename)
  )
)

const partnersCodenames = partnersFiltered.map((m) => m.module)
const currentCodename = page.system.codename
const nextIndex = (partnersCodenames.indexOf(currentCodename) + 1) % partnersCodenames.length
const nextRoute =
  links.data.value?.data?.find((l) => l.codename == partnersCodenames[nextIndex])?.path ?? ''
const prevIndex =
  (partnersCodenames.indexOf(currentCodename) - 1 + partnersCodenames.length) %
  partnersCodenames.length
const prevRoute =
  links.data.value?.data?.find((l) => l.codename == partnersCodenames[prevIndex])?.path ?? ''
const nextPartnerUrl = ref(nextRoute)
const prevPartnerUrl = ref(prevRoute)
</script>
